import { toJS } from 'mobx';
import UserApi from '../../rest-api/user-api';

/**
 * Archives a customised handout
 * Similar to whiteboard archive functionality
 */
export const archiveCustomisedHandout = async (
    ch,
    userDataStore,
    customisedHandoutStore,
    authStore,
    setCustomisedHandouts,
    setFilteredHandouts,
    showSnackbar,
) => {
    try {
        // Minimal payload - only required field is baseContentId
        const payload = {
            customisedId: ch.customisedId,
            baseContentId: ch.baseContentId,
            customisedStatus: 'archived',
        };

        await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, payload);
        console.warn('Archive customised handout response');

        // Deep serialize to remove all MobX observables and non-serializable properties
        const plainCH = JSON.parse(JSON.stringify(toJS(ch)));
        const { type, ...rest } = plainCH;
        const archivedCH = { ...rest, customisedStatus: 'archived' };
        await customisedHandoutStore.setCustomisedHandoutData([archivedCH]);

        // Remove from UI lists
        setCustomisedHandouts((prev) => prev.filter((old) => old.customisedId !== ch.customisedId));
        setFilteredHandouts((prev) => prev.filter((old) => old.customisedId !== ch.customisedId));

        // Only update userData.customisedHandouts if this CH was in "My Library" (no patientId)
        // Patient CHs only appear in "My Clinic", not "My Library"
        if (!ch.patientId) {
            const updatedCustomisedHandouts = userDataStore.userData.customisedHandouts?.filter((id) => id !== ch.customisedId) || [];
            const updatedFavourites = userDataStore.userData.customisedHandoutsFavourites?.filter((id) => id !== ch.customisedId) || [];

            userDataStore.setUserData({
                ...userDataStore.userData,
                customisedHandouts: updatedCustomisedHandouts,
                customisedHandoutsFavourites: updatedFavourites,
            });

            const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
            const updatePayload = {
                customisedHandouts: updatedCustomisedHandouts,
                customisedHandoutsFavourites: updatedFavourites,
            };

            try {
                if (isMultiUser) {
                    updatePayload.action = 'updateMultiuser';
                    updatePayload.emailAddress = userDataStore.userData.emailAddress;
                    updatePayload.inviteId = userDataStore.userData.inviteId;
                    await UserApi.updateClinicianDataMultiuser(authStore.idToken, updatePayload);
                } else {
                    await UserApi.updateClinicianData(authStore.idToken, updatePayload);
                }
            } catch (err) {
                console.error('Failed to sync archived customised handout update to backend', err);
            }
        }

        showSnackbar && showSnackbar('Customised handout archived successfully', 'success');
    } catch (err) {
        console.error('Failed to archive customised handout', err);
        showSnackbar && showSnackbar('Failed to archive customised handout', 'error');
    }
};

/**
 * Recovers (unarchives) a customised handout
 * Similar to whiteboard recover functionality
 */
export const recoverCustomisedHandout = async (
    ch,
    userDataStore,
    customisedHandoutStore,
    authStore,
    setArchivedCustomisedHandouts,
    setFiltered,
    showSnackbar,
) => {
    try {
        // Minimal payload - only required field is baseContentId
        const payload = {
            customisedId: ch.customisedId,
            baseContentId: ch.baseContentId,
            customisedStatus: 'active',
        };

        await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, payload);
        console.warn('Recover customised handout response');

        // Deep serialize to remove all MobX observables and non-serializable properties
        const plainCH = JSON.parse(JSON.stringify(toJS(ch)));
        const { type, ...rest } = plainCH;
        const recoveredCH = { ...rest, customisedStatus: 'active' };
        await customisedHandoutStore.setCustomisedHandoutData([recoveredCH]);

        // Remove from archived lists
        setArchivedCustomisedHandouts((prev) => prev.filter((c) => c.customisedId !== ch.customisedId));
        setFiltered((prev) => prev.filter((c) => c.customisedId !== ch.customisedId));

        // Only add back to user's active list if it's NOT assigned to a patient
        // CHs with patientId should only appear in "My Clinic", not "My Library"
        if (!ch.patientId) {
            const updatedCustomisedHandouts = [...(userDataStore.userData.customisedHandouts || []), ch.customisedId];

            userDataStore.setUserData({
                ...userDataStore.userData,
                customisedHandouts: updatedCustomisedHandouts,
            });

            // Sync to backend
            const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
            const updatePayload = {
                customisedHandouts: updatedCustomisedHandouts,
            };

            try {
                if (isMultiUser) {
                    updatePayload.action = 'updateMultiuser';
                    updatePayload.emailAddress = userDataStore.userData.emailAddress;
                    updatePayload.inviteId = userDataStore.userData.inviteId;
                    await UserApi.updateClinicianDataMultiuser(authStore.idToken, updatePayload);
                } else {
                    await UserApi.updateClinicianData(authStore.idToken, updatePayload);
                }
            } catch (err) {
                console.error('Failed to sync recovery to backend', err);
            }
        }

        showSnackbar && showSnackbar('Customised handout recovered!', 'success');
    } catch (err) {
        console.error('Failed to recover customised handout', err);
        showSnackbar && showSnackbar('Failed to recover customised handout', 'error');
    }
};
